import { Dispatch, FC, SetStateAction, useContext, useEffect, useRef, useState } from "react";
import { makeStyles } from "tss-react/mui";
import { ILessonEntity, ILessonEntityPayload } from "@buildwitt/shapeshift-types";
import { AdminEntityPageContainer, AdminEntityPageContainerProps, ConfirmDeleteEntityDialog } from "@buildwitt/shapeshift-common/v2/admin";
import { cohortLessonValidationDoc } from "../../../validationDocs/user/cohortLessonValidationDoc";
import { Link } from "react-router-dom";
import { Accordion, AccordionDetails, AccordionSummary, Box, Button, Chip, Stack, Typography, useEventCallback } from "@mui/material";
import { AdminHeader } from "@/pages/ManagerView/ContentCreation/AdminHeader";
import { AdminFormContainer } from "@/pages/ManagerView/ContentCreation/AdminFormContainer";
import PlayCircleFilledWhiteOutlinedIcon from "@mui/icons-material/PlayCircleFilledWhiteOutlined";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import { AttachQuizInline } from "@/pages/ManagerView/ContentCreation/AttachQuizInline";
import { AdminInputText } from "@/pages/ManagerView/ContentCreation/AdminInputText";
import { AuthContext, IconContext } from "@buildwitt/shapeshift-identity";
import { UploadBrightcoveVideo } from "@/pages/ManagerView/ContentCreation/UploadBrightcoveVideo";
import { palette } from "@/constants";
import { VideoUploadType } from "@/types/video";
import ImageSelector from "@/pages/ManagerView/ContentCreation/ImageSelector";
import { IDropzoneFile } from "@buildwitt/shapeshift-common/v1/fileDropzone/fileDropzoneFile";
import { AdminInputSwitch } from "@/pages/ManagerView/ContentCreation/AdminInputSwitch";
import { AttachLinks } from "./AttachLinks";
import { AttachFiles } from "./AttachFiles";
import useDevCycleSdk from "@/hooks/useDevCycleSdk";

const useStyles = makeStyles()((theme) => ({
	formFieldset: {
		borderRadius: "4px",
		"&.Mui-expanded": {
			margin: 0
		}
	},
	formFieldsetSummary: {
		padding: theme.spacing(0, 3),
		".MuiAccordionSummary-content": {
			display: "flex",
			flexDirection: "column",
			gap: theme.spacing(1)
		}
	},
	subtitle: {
		opacity: 0.7
	},
	formFieldsetDetails: {
		display: "flex",
		flexDirection: "column",
		gap: theme.spacing(4),
		paddingLeft: theme.spacing(3),
		paddingRight: theme.spacing(3),
		paddingTop: theme.spacing(2),
		paddingBottom: theme.spacing(3)
	},
	formFieldsetNumber: {
		lineHeight: 1,
		display: "flex",
		flexDirection: "column",
		alignItems: "center",
		justifyContent: "center",
		height: "24px",
		marginRight: theme.spacing(2),
		color: theme.palette.primary.contrastText,
		borderRadius: "100%",
		aspectRatio: "1 / 1",
		backgroundColor: theme.palette.primary.main
	},
	uploadBtn: {
		height: "56px",
		color: theme.palette.primary.main,
		"&:hover": {
			backgroundColor: "transparent"
		},
		"&.Mui-disabled": {
			borderColor: palette.white,
			opacity: 0.38
		},
		flexShrink: 0
	},
	videoUrlChip: {
		border: `1px solid ${theme.palette.primary.main}`,
		".MuiChip-deleteIcon": {
			"&, &:hover": {
				color: theme.palette.primary.main
			}
		}
	},
	addThumbnailBtn: {
		position: "absolute",
		transform: "translateY(40%) translateX(40%)",
		bottom: "0",
		right: "0",
		height: "32px",
		width: "48px",
		minWidth: "unset",
		".MuiButton-startIcon": {
			margin: 0
		}
	},
	uploadRow: {
		flexDirection: "column",
		alignItems: "flex-start",
		rowGap: theme.spacing(2),
		[theme.breakpoints.up("sm")]: {
			flexDirection: "row",
			alignItems: "flex-start"
		}
	},
	thumbnailContainer: {
		border: `1px solid ${theme.palette.grey[200]}`,
		alignSelf: "flex-start",
		padding: theme.spacing(2, 6),
		position: "relative",
		boxSizing: "border-box",
		[theme.breakpoints.up("md")]: {
			aspectRatio: "1.8"
		},
		borderRadius: "4px",
		height: "176px",
		maxWidth: "100%",
		"img": {
			width: "100%",
			height: "100%",
			objectFit: "contain",
			objectPosition: "center"
		}
	},
	thumbnailContainerLabel: {
		position: "absolute",
		top: "0",
		left: "8px",
		transform: "translateY(-50%)",
		backgroundColor: palette.greyDark,
		padding: theme.spacing(0, 0.5),
		fontSize: "12px"
	},
	uploadInput: {
		flexGrow: 1,
		marginRight: theme.spacing(2),
		".MuiFormHelperText-root": {
			color: "#F9F9F9B2"
		}
	},
	uploadInputChip: {
		"input": {
			opacity: 0
		}
	},
	expandIcon: {}
}));

interface ILessonAdminPageEntry extends ILessonEntity { //> to do move to types package
	enrollmentCount?: number
}

interface IProps extends Partial<AdminEntityPageContainerProps<ILessonEntity, "lesson">> {
	lessonId?: number,
	className?: string
}

const template: ILessonEntityPayload = {
	title: "", slug: "", content: ""
}

const StateUpdater = ({ disablePublish, setPublishDisabled }: { disablePublish: boolean, setPublishDisabled: Dispatch<SetStateAction<boolean>> }) => {
	useEffect(() => {
		setPublishDisabled(disablePublish);
	}, [disablePublish, setPublishDisabled]);
	// This component is used to update the state of the parent component
	// It is a workaround for the fact that the parent component does not re-render when the state changes
	return null;
}

export const AdminLessonPageContent: FC<IProps> = ({ lessonId, ...props }) => {
	const { classes, cx } = useStyles();
	const { icon } = useContext(IconContext);
	const { jwt } = useContext(AuthContext);
	const [uploadState, setUploadState] = useState<VideoUploadType>("idle");
	const [videoId, setVideoId] = useState<string | null>(null);
	const [autogenVideoUrl, setAutogenVideoUrl] = useState<string | null>(null);
	const [enabledAutogen, setEnabledAutogen] = useState<boolean>(true);
	const [isPublishDisabled, setPublishDisabled] = useState<boolean>(true);
	const intervalRef = useRef<number | null>(null);
	const { lessonLinksAttachments } = useDevCycleSdk();
	const ILessonEntityToILessonEntityPayload = (e: ILessonEntity): ILessonEntityPayload => {
		const { title, slug, content, id, createdAt, updatedAt, subtitle, quiz, videoType, videoUrl, videoThumbnailUrl, links } = e
		const videoData = (e as any).videoData;
		const payload = {
			title, slug, content, id, createdAt, updatedAt, subtitle, videoData, quiz: quiz?.id, videoType, videoUrl, videoThumbnailUrl, links
		}
		return payload;
	}

	const fetchData = useEventCallback(async () => {
		try {
			const res = await fetch(`${process.env.REACT_APP_API_HOST}/api/lesson/video/brightcove/query`, {
				method: "POST",
				body: JSON.stringify({
					videoId
				}),
				headers: {
					"Content-Type": "application/json",
					"Authorization": `Bearer ${jwt}`
				}
			});
			if (!res.ok) {
				return;
			}

			const data = await res.json();
			if (!data?.thumbnailUrl) {
				setAutogenVideoUrl(null)
			}
			if (data?.thumbnailUrl && autogenVideoUrl !== data?.thumbnailUrl) {
				setAutogenVideoUrl(data.thumbnailUrl);
			}
		} catch (err) {
			// just ignore errors
		}
	});

	useEffect(() => {
		if (intervalRef.current) {
			clearInterval(intervalRef.current);
			intervalRef.current = null;
		}

		if (!videoId) {
			return;
		}

		// fetches video data every second
		intervalRef.current = window.setInterval(fetchData, 1000);

		return () => {
			if (intervalRef.current) {
				clearInterval(intervalRef.current);
			}
		};
	}, [videoId])

	return (
		<AdminEntityPageContainer<ILessonAdminPageEntry, "lesson">
			entityId={lessonId}
			entityToPayload={ILessonEntityToILessonEntityPayload}
			template={template}
			validationDoc={cohortLessonValidationDoc}
			type="lesson"
			componentOverrides={{
				adminHeader: (props) =>
					<AdminHeader rootSlug="/admin/lessons" rootBreadcrumb={<><PlayCircleFilledWhiteOutlinedIcon /><Link to={"/admin/lessons"}><Typography variant="body1">Lessons</Typography></Link></>} editMode={lessonId ? "edit" : "create"} {...props} />,
				adminFormContainer: ({ entity, ...containerProps }) =>
					<AdminFormContainer<ILessonAdminPageEntry, "lesson"> {...containerProps} entity={entity} disablePublish={isPublishDisabled} rootSlug="/admin/lessons" componentOverrides={{ deleteDialog: deleteProps => <ConfirmDeleteEntityDialog {...deleteProps} componentOverrides={{ content: entity?.enrollmentCount && entity?.enrollmentCount >= 1 ? <Typography variant="body1">You will be making an assigned lesson unavailable.</Typography> : <Typography variant="body1">Do you want to delete?</Typography> }} /> }} />
			}}
			{...props}
		>
			{({ formData, setFormData, entity, files, setFiles, validation }) => {
				const isBrightcoveVideo = formData.videoType === "brightcove" && !!formData.videoUrl;
				const allowThumbnailUpload = uploadState !== "progress" && !enabledAutogen;
				const disableUpload = (formData.videoType !== "brightcove" && formData.videoUrl && uploadState === "idle") || uploadState === "progress";
				const inputReadOnly = isBrightcoveVideo || uploadState === "progress";
				const uploadLabel = uploadState === "progress" ? "Uploading..." : "Upload file";
				const noCurrentUploadedThumbnail = !videoId || videoId === entity?.videoUrl;
				const existingPreviewUrl = noCurrentUploadedThumbnail ? entity?.videoThumbnailUrl : null;
				const uploadedThumbnailUrl = entity?.moduleThumbnailOverride?.url ?? null;
				const disablePublish = isBrightcoveVideo && !(existingPreviewUrl || autogenVideoUrl);

				const handleUpload = (videoId: string): void => {
					setVideoId(videoId);
					setFormData(prev => ({ ...prev, videoType: "brightcove", videoUrl: videoId }))
				}

				const handleClear = (): void => {
					setVideoId(null);
					setFormData(prev => ({ ...prev, videoType: "url", videoUrl: "" }))
				}

				const handleImageChange = (files?: IDropzoneFile[]): void => {
					setFiles(prev => ({ ...prev, moduleThumbnailOverride: files ?? null }));
					if (!files || files.length === 0) {
						setFormData(prev => ({ ...prev, moduleThumbnailOverride: null }));
					}
				}

				const chipVideoUrl = formData.videoUrl ? <Chip
					className={classes.videoUrlChip}
					label={formData.videoUrl}
					onDelete={handleClear}
				/> : null

				return <>
					<StateUpdater disablePublish={disablePublish} setPublishDisabled={setPublishDisabled} />
					<Accordion defaultExpanded className={classes.formFieldset}>
						<AccordionSummary
							className={classes.formFieldsetSummary}
							expandIcon={icon("expandMoreIcon", {
								className: classes.expandIcon
							})}
						>
							<Stack direction="row" alignItems="center"><span className={classes.formFieldsetNumber}>1</span><Typography variant="h6" component="p">Lesson Information</Typography></Stack>
							<Typography className={classes.subtitle}>Add the basic details for your lesson in English.</Typography>
						</AccordionSummary>
						<AccordionDetails className={classes.formFieldsetDetails}>
							<AdminInputText error={validation?.[0]?.title} required onChange={e => setFormData(prev => ({ ...prev, title: e.target.value }))} value={formData.title} label={"Lesson title"} />
							<AdminInputText error={validation?.[0]?.subtitle} multiline rows={5} label="Description" onChange={e => setFormData(prev => ({ ...prev, subtitle: e.target.value }))} value={formData.subtitle} />
						</AccordionDetails>
					</Accordion>

					<Accordion className={classes.formFieldset}>
						<AccordionSummary
							className={classes.formFieldsetSummary}
							expandIcon={icon("expandMoreIcon", {
								className: classes.expandIcon
							})}
						>
							<Stack direction="row" alignItems="center"><span className={classes.formFieldsetNumber}>2</span>
								<Typography variant="h6" component="p">Lesson Media</Typography></Stack>
							<Typography className={classes.subtitle}>Upload your lesson video and thumbnail image.</Typography>
						</AccordionSummary>
						<AccordionDetails className={classes.formFieldsetDetails}>
							<Stack className={classes.uploadRow}>
								<AdminInputText className={cx(classes.uploadInput, isBrightcoveVideo && classes.uploadInputChip)} error={validation?.[0]?.videoUrl} required onChange={e => setFormData(prev => ({ ...prev, videoUrl: e.target.value, videoType: "url" }))} value={formData.videoUrl} InputProps={{ readOnly: inputReadOnly, startAdornment: isBrightcoveVideo ? chipVideoUrl : null }} label={"Video"} helperText="Upload a video file (.mp4, .mov, .avi) or paste a YouTube link (copy it from the browser address bar, not the 'Share' button). Max file size: 10GB." />
								<UploadBrightcoveVideo uploadState={uploadState} setUploadState={setUploadState} handleSuccess={handleUpload} lessonId={entity?.id ?? 0}>{({ onClick, progress, error: uploadError }) => <Stack flexShrink={0}><Button variant="outlined" className={classes.uploadBtn} onClick={onClick} startIcon={<UploadFileOutlinedIcon />} disabled={disableUpload}>{uploadLabel}</Button>{uploadError ? <Typography color="error.main" variant="body2">{uploadError}</Typography> : null}</Stack>}
								</UploadBrightcoveVideo>
							</Stack>
							{disablePublish ? <Typography>The video is still processing. Please wait until the processing has completed to publish the lesson</Typography> : null}
							<AdminInputSwitch label="Use the auto-generated or fallback video thumbnail" onChange={(e) => setEnabledAutogen(prev => !prev)} checked={enabledAutogen} />
							<ImageSelector disabled={!allowThumbnailUpload} label="Thumbnail" defaultImageUrl={uploadedThumbnailUrl ?? existingPreviewUrl ?? autogenVideoUrl ?? undefined} onImageSelected={handleImageChange} showDeleteIcon={uploadedThumbnailUrl && allowThumbnailUpload} />
							<Box sx={{ marginBottom: "20px" }} />

						</AccordionDetails>
					</Accordion>

					<Accordion className={classes.formFieldset}>
						<AccordionSummary
							className={classes.formFieldsetSummary}
							expandIcon={icon("expandMoreIcon", {
								className: classes.expandIcon
							})}
						>
							<Stack direction="row" alignItems="center"><span className={classes.formFieldsetNumber}>3</span><Typography>Include Quiz (Optional)</Typography></Stack>
							<Typography className={classes.subtitle}>Include quizzes to support learning.</Typography>
						</AccordionSummary>
						<AccordionDetails className={classes.formFieldsetDetails}>
							<AttachQuizInline
								quizId={formData.quiz}
								onChange={(id) => { setFormData(prev => ({ ...prev, quiz: id })) }} />
						</AccordionDetails>
					</Accordion>

					{lessonLinksAttachments && (
						<>
							<Accordion className={classes.formFieldset}>
								<AccordionSummary
									className={classes.formFieldsetSummary}
									expandIcon={icon("expandMoreIcon", {
										className: classes.expandIcon
									})}
								>
									<Stack direction="row" alignItems="center"><span className={classes.formFieldsetNumber}>4</span><Typography>Include Links (Optional)</Typography></Stack>
									<Typography className={classes.subtitle}>Include links to support learning.</Typography>
								</AccordionSummary>
								<AccordionDetails className={classes.formFieldsetDetails}>
									<AttachLinks
										links={formData.links || []}
										onChange={(links) => { setFormData(prev => ({ ...prev, links: links })) }} />
								</AccordionDetails>
							</Accordion>

							<Accordion className={classes.formFieldset}>
								<AccordionSummary
									className={classes.formFieldsetSummary}
									expandIcon={icon("expandMoreIcon", {
										className: classes.expandIcon
									})}
								>
									<Stack direction="row" alignItems="center"><span className={classes.formFieldsetNumber}>5</span><Typography>Include Files (Optional)</Typography></Stack>
									<Typography className={classes.subtitle}>Include files to support learning.</Typography>
								</AccordionSummary>
								<AccordionDetails className={classes.formFieldsetDetails}>
									<AttachFiles
										value={entity?.files || []}
										onChange={(files) => {
											// Extract the IDs from existing files
											const existingFileIds = files.filter(f => f.id).map(f => f.id);

											// Separate new files to upload
											const newClientFiles = files.filter(f => !f.id)

											setFiles(prev => ({ ...prev, files: newClientFiles }));

											setFormData(prev => ({
												...prev,
												files: files,
												existingFileIds
											}));
										}}
									/>
								</AccordionDetails>
							</Accordion>
						</>
					)}
				</>
			}}
		</AdminEntityPageContainer >
	);
};
